import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { Button } from '@/components/ui/button';
import { getSchool } from '@/lib/api-calls';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { ClassTimesTable } from '@/routes/_layout/orgs/schools/-components/class-times-table';
import { CreateEditClassTime } from '@/routes/_layout/orgs/schools/-components/create-edit-class-time';
import { CreateEditSchedule } from '@/routes/_layout/scheduling/schedules/-components/create-edit-schedule';
import { formatScheduleBreak, formatScheduleDates } from '@/routes/_layout/scheduling/schedules/-components/formatting';
import { formatPlainDate, isAirAdmin } from '@airreading/common';
import { ClassTime, Schedule } from '@airreading/common/data';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Edit } from 'lucide-react';
import { useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { validateRouteRole } from '@/lib/routing';
import { schoolDetailsTabs } from '../-components/tabs';

export const Route = createFileRoute('/_layout/orgs/schools/$schoolId/schedules')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchoolSchedulesPage,
});

interface AddEditScheduleProps {
  mode: 'edit' | 'create';
  schedule: Schedule;
}

interface AddEditClassTimeProps {
  mode: 'edit' | 'create';
  classTime: ClassTime;
}

function SchoolSchedulesPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load school. Please try again.',
          back: {
            backLink: {
              to: '/orgs',
            },
          },
        };
      }}
    >
      <SchoolSchedules />
    </InfoPageSuspenseBoundary>
  );
}

function SchoolSchedules() {
  const { schoolId } = Route.useParams();
  const { data } = useSuspenseQuery(getSchool(schoolId));
  const queryClient = useQueryClient();

  const schedules = data.schedules
    .filter((s) => s.schoolId === data.id)
    .sort((a, b) => Temporal.PlainDate.compare(b.startPlainDate, a.startPlainDate));
  const [refreshKeys, setRefreshKeys] = useState<Record<string, number>>({});
  const bumpRefreshKeyFor = (scheduleId: string) => {
    setRefreshKeys((prev) => ({
      ...prev,
      [scheduleId]: (prev[scheduleId] ?? 0) + 1,
    }));
  };

  const refetchSchool = () => {
    queryClient.invalidateQueries(getSchool(schoolId));
    queryClient.invalidateQueries({ queryKey: ['AVAILABLE_COACHES'] });
  };

  const emptySchedule: Schedule = {
    id: '',
    name: '',
    timeZone: '',
    practicePlainDate: null,
    diagnosticPlainDate: null,
    schoolId: data.id,
    status: 'open' as const,
    startPlainDate: Temporal.Now.plainDateISO(),
    endPlainDate: Temporal.Now.plainDateISO(),
    scheduleBreaks: [],
    school: data,
    classTimes: [],
  };

  const AddEditSchedule = ({ mode, schedule }: AddEditScheduleProps) => {
    const button =
      mode === 'create' ? (
        <Button>Add Schedule</Button>
      ) : (
        <Button size="sm" icon={Edit} variant="secondary">
          Edit
        </Button>
      );

    return (
      <CreateEditSchedule
        from={{
          fromSchedule: { ...schedule, school: data },
          mode,
        }}
        trigger={button}
        afterSave={() => {
          refetchSchool();
          if (mode === 'edit') bumpRefreshKeyFor(schedule.id);
        }}
        modalTitle={mode === 'edit' ? 'Edit schedule' : 'Create schedule'}
      />
    );
  };

  const emptyClassTime: ClassTime = {
    id: '',
    scheduleId: '',
    type: 'recurring',
    meetingBlocks: [],
    schedule: emptySchedule,
  };

  function AddEditClassTimes({ mode, classTime }: AddEditClassTimeProps) {
    const button = <Button size="sm">Add class times</Button>;
    return (
      <CreateEditClassTime
        from={{
          fromClassTime: classTime,
          mode,
        }}
        trigger={button}
        afterSave={() => {
          refetchSchool();
          if (mode === 'edit') {
            bumpRefreshKeyFor(classTime.scheduleId);
          }
        }}
        schoolTimeZone={data.org.timeZone}
      />
    );
  }

  return (
    <InfoPage
      headerText={data.name || data.id}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Schools',
            link: {
              to: '/orgs/schools',
            },
          },
        ],
        currentPage: data.name,
      }}
      tabs={schoolDetailsTabs({ schoolId: data.id })}
    >
      <InfoCard
        headerText="Schedules"
        maxItemsPerRow={1}
        headerActions={<AddEditSchedule mode="create" schedule={emptySchedule} />}
      >
        {schedules.map((schedule) => {
          const { id, name, practicePlainDate, diagnosticPlainDate, scheduleBreaks } = schedule;
          const elementId = `${id}-${refreshKeys[id] ?? 0}`;
          const regularDates = formatScheduleDates(schedule);
          const practiceDate = practicePlainDate ? formatPlainDate(practicePlainDate) : '';
          const diagnosticDate = diagnosticPlainDate ? formatPlainDate(diagnosticPlainDate) : '';

          return (
            <InfoCard
              key={`schedule-${elementId}`}
              headerText={name}
              headerActions={
                <div className="flex gap-2">
                  <AddEditClassTimes mode="create" classTime={{ ...emptyClassTime, scheduleId: id }} />
                  <AddEditSchedule mode="edit" schedule={schedule} />
                </div>
              }
            >
              <InfoKeyValue label="Regular class dates" value={regularDates} />
              <InfoKeyValue label="Practice date" value={practiceDate} />
              <InfoKeyValue label="Diagnostic date" value={diagnosticDate} />
              <InfoKeyValue
                label="Break"
                value={
                  <>
                    {scheduleBreaks.map((scheduleBreak, i) => {
                      return <div key={`schedule-break-${elementId}-${i}`}>{formatScheduleBreak(scheduleBreak)}</div>;
                    })}
                  </>
                }
              />
              <div className="w-full col-span-4">
                <ClassTimesTable scheduleId={id} classTimeTypes={['recurring']} schoolTimeZone={data.org.timeZone} />
              </div>
            </InfoCard>
          );
        })}
      </InfoCard>
    </InfoPage>
  );
}
